import {
  type Dispatch,
  type ElementType,
  type MutableRefObject,
  type SetStateAction,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useState,
} from "react";

import {
  ArrayIcon,
  Box,
  Column,
  ObjectIcon,
  Row,
  TemplatesIcon,
  TextStringIcon,
  useToast,
  VariableIcon,
} from "@hightouch/htui";

import {
  type ColumnOption,
  useFormkitContext,
  useFormkitDataContext,
} from "src/formkit/components/formkit-context";
import { ArrayPane } from "src/formkit/components/mapper-v2/array-pane";
import { ArrayPropertiesPane } from "src/formkit/components/mapper-v2/array-properties-pane";
import {
  buildColumnCategories,
  type ColumnCategory,
} from "src/formkit/components/mapper-v2/column-categories";
import { ColumnPane } from "src/formkit/components/mapper-v2/column-pane";
import {
  type MapperFocusTarget,
  MapperListSkeleton,
} from "src/formkit/components/mapper-v2/mapper-list";
import {
  MapperNav,
  type MapperNavOtherItem,
} from "src/formkit/components/mapper-v2/mapper-nav";
import {
  getDraftForSelection,
  getInitialSelection,
  getNavItemKey,
  getNavTabId,
  isSameSelection,
  type MapperValue,
  type NavSelection,
  OTHER_VALUE_TYPES,
  type OtherValueType,
  resolveAppliedValue,
} from "src/formkit/components/mapper-v2/mapper-selection";
import { ObjectPane } from "src/formkit/components/mapper-v2/object-pane";
import { StaticPane } from "src/formkit/components/mapper-v2/static-pane";
import { TemplatePane } from "src/formkit/components/mapper-v2/template-pane";
import { variableMappingLabel } from "src/formkit/components/mapper-v2/variable-labels";
import { resolveVariableScope } from "src/formkit/components/mapper-v2/variable-options";
import { VariablePane } from "src/formkit/components/mapper-v2/variable-pane";
import {
  type JsonColumnProps,
  type Mapping,
  MappingType,
  type TemplateInfo,
} from "src/formkit/components/types";

const OTHER_VALUE_META: Record<
  OtherValueType,
  { label: string; icon: ElementType }
> = {
  [MappingType.STATIC]: { label: "Static value", icon: TextStringIcon },
  [MappingType.VARIABLE]: { label: "Variable value", icon: VariableIcon },
  [MappingType.TEMPLATE]: { label: "Template", icon: TemplatesIcon },
  [MappingType.OBJECT]: { label: "Create an object", icon: ObjectIcon },
  [MappingType.ARRAY]: { label: "Create an array", icon: ArrayIcon },
};

export type MapperPopoverContentProps = {
  /** The committed mapping value. The popover edits a draft of it. */
  value: MapperValue;
  columnOptions: ColumnOption[] | undefined;
  /** Rows inside an array mapping pick the array's element properties instead of model columns. */
  overwriteColumnsWithArrayProps: boolean | undefined;
  enabledTypes: ReadonlySet<MappingType>;
  /** The popover's initial focus target, which the active pane attaches to its main control. */
  focusRef: MutableRefObject<MapperFocusTarget | null>;
  jsonColumnProperties: JsonColumnProps;
  onChangeJsonColumnProperties: Dispatch<SetStateAction<JsonColumnProps>>;
  onReloadEligibleInlineMapperColumns: (currentSelectedColumn?: string) => void;
  parentMapping: Mapping | undefined;
  templates: TemplateInfo[];
  onCommit: (value: MapperValue) => void;
  onClose: () => void;
};

/**
 * The popover's body. It mounts each time the popover opens, so every open
 * starts from the committed value.
 */
export function MapperPopoverContent(props: MapperPopoverContentProps) {
  const { columnOptions, enabledTypes, overwriteColumnsWithArrayProps } = props;
  const { columns: contextColumns, model } = useFormkitContext();
  const { loadingModel } = useFormkitDataContext();

  const isEventModel = Boolean(model?.isEventModel);

  const isStandardEnabled = enabledTypes.has(MappingType.STANDARD);
  const columnSource = overwriteColumnsWithArrayProps
    ? columnOptions
    : (columnOptions ?? contextColumns);
  const categories = useMemo(
    () =>
      isStandardEnabled
        ? buildColumnCategories(columnSource, {
            mode: overwriteColumnsWithArrayProps
              ? "array-properties"
              : "columns",
            isEventModel,
          })
        : [],
    [
      columnSource,
      isEventModel,
      isStandardEnabled,
      overwriteColumnsWithArrayProps,
    ],
  );

  // An event model lists an empty "Event properties" entry until its suggestions load, so this counts options.
  const hasColumns = categories.some((category) => category.options.length > 0);
  // The opening pane depends on the columns, so the panes wait for them; array property rows show their own loading rows.
  // The wait ends once the panes mount, so a later reload never drops a draft.
  const isLoadingCategories =
    isStandardEnabled &&
    !overwriteColumnsWithArrayProps &&
    !hasColumns &&
    loadingModel;
  const [openedWhileLoading] = useState(isLoadingCategories);
  const [isWaiting, setIsWaiting] = useState(isLoadingCategories);
  if (isWaiting && !isLoadingCategories) {
    setIsWaiting(false);
  }

  if (isWaiting) {
    return (
      <Row height="100%" width="100%">
        <Box
          bg="base.background"
          borderColor="base.divider"
          borderRightStyle="solid"
          borderRightWidth="hairline"
          flexShrink={0}
          width="2xs"
        />
        <Column flex={1} minWidth={0}>
          <MapperListSkeleton />
        </Column>
      </Row>
    );
  }
  return (
    <MapperPanes
      {...props}
      categories={categories}
      columnSource={columnSource}
      focusOnMount={openedWhileLoading}
    />
  );
}

type MapperPanesProps = MapperPopoverContentProps & {
  categories: ColumnCategory[];
  columnSource: ColumnOption[] | undefined;
  /** Moves focus into the opening pane, which Chakra's initial focus misses when the panes mount late. */
  focusOnMount: boolean;
};

/** The nav and the selected pane, which own the selection and the draft. */
function MapperPanes({
  value,
  enabledTypes,
  focusRef,
  columnOptions,
  jsonColumnProperties,
  onChangeJsonColumnProperties,
  onReloadEligibleInlineMapperColumns,
  overwriteColumnsWithArrayProps,
  parentMapping,
  templates,
  onCommit,
  onClose,
  categories,
  columnSource,
  focusOnMount,
}: MapperPanesProps) {
  const { columns: contextColumns, model, variableScope } = useFormkitContext();
  const { loadingRows } = useFormkitDataContext();
  const { toast } = useToast();
  const idPrefix = useId();
  const tabPanelId = `${idPrefix}-panel`;

  const scope = resolveVariableScope({ variableScope, model });

  const otherItems = OTHER_VALUE_TYPES.filter((type) =>
    enabledTypes.has(type),
  ).map(
    (type): MapperNavOtherItem => ({
      type,
      icon: OTHER_VALUE_META[type].icon,
      label:
        type === MappingType.VARIABLE
          ? variableMappingLabel(scope)
          : OTHER_VALUE_META[type].label,
    }),
  );

  const [{ selection, draft }, setState] = useState(() => {
    const initialSelection = getInitialSelection({
      value,
      enabledTypes,
      categories,
    });
    return {
      selection: initialSelection,
      draft: getDraftForSelection(value, initialSelection),
    };
  });

  // One search spans every column category, so it lives above the panes, which remount on each change.
  const [columnSearch, setColumnSearch] = useState("");

  const updateDraft = useCallback(
    (update: (draft: MapperValue) => MapperValue) => {
      setState((previous) => ({ ...previous, draft: update(previous.draft) }));
    },
    [],
  );

  const setFocusTarget = useCallback(
    (target: MapperFocusTarget | null) => {
      focusRef.current = target;
    },
    [focusRef],
  );

  // Focus moves into a pane once it has mounted, after a pointer selection or when the panes mount late.
  const [paneFocusRequest, setPaneFocusRequest] = useState(
    focusOnMount ? 1 : 0,
  );
  useEffect(() => {
    if (paneFocusRequest > 0) focusRef.current?.focus();
  }, [focusRef, paneFocusRequest]);

  const select = (nextSelection: NavSelection, fromPointer: boolean) => {
    if (!isSameSelection(nextSelection, selection)) {
      setState((previous) => ({
        selection: nextSelection,
        draft: getDraftForSelection(previous.draft, nextSelection),
      }));
    }
    if (fromPointer) setPaneFocusRequest((request) => request + 1);
  };

  const apply = (valueToApply: MapperValue) => {
    const result = resolveAppliedValue({ draft, valueToApply, parentMapping });
    if (!result.ok) {
      toast({
        id: "array-inline-mapper",
        title: result.message,
        variant: "error",
      });
      return;
    }
    onCommit(result.value);
    onClose();
  };

  const renderPane = () => {
    if (selection.kind === "column") {
      const category = categories.find(
        (candidate) => candidate.id === selection.categoryId,
      );
      return overwriteColumnsWithArrayProps ? (
        <ArrayPropertiesPane
          category={category}
          jsonColumnProperties={jsonColumnProperties}
          search={columnSearch}
          searchRef={setFocusTarget}
          value={value}
          onChoose={apply}
          onClose={onClose}
          onReloadEligibleInlineMapperColumns={
            onReloadEligibleInlineMapperColumns
          }
          onSearchChange={setColumnSearch}
        />
      ) : (
        <ColumnPane
          category={category}
          columnGroups={columnSource}
          draftType={draft.type ?? MappingType.STANDARD}
          search={columnSearch}
          searchRef={setFocusTarget}
          value={value}
          onChoose={apply}
          onClose={onClose}
          onSearchChange={setColumnSearch}
        />
      );
    }

    switch (selection.type) {
      case MappingType.STATIC:
        return (
          <StaticPane
            inputRef={setFocusTarget}
            value={draft}
            onApply={() => apply(draft)}
            onChange={(nextDraft) => updateDraft(() => nextDraft)}
          />
        );
      case MappingType.VARIABLE:
        return (
          <VariablePane
            scope={scope}
            searchRef={setFocusTarget}
            value={value}
            onChoose={apply}
            onClose={onClose}
          />
        );
      case MappingType.TEMPLATE:
        return (
          <TemplatePane
            columns={
              overwriteColumnsWithArrayProps
                ? (columnOptions ?? [])
                : contextColumns
            }
            isLoadingColumns={Boolean(
              overwriteColumnsWithArrayProps && loadingRows,
            )}
            editorRef={setFocusTarget}
            isRealtimeSync={scope === "realtime-audience"}
            templates={templates}
            updateDraft={updateDraft}
            value={draft}
            onApply={() => apply(draft)}
          />
        );
      case MappingType.OBJECT:
        return (
          <ObjectPane
            buttonRef={setFocusTarget}
            onCreate={() => apply(draft)}
          />
        );
      case MappingType.ARRAY:
        return (
          <ArrayPane
            columnOptions={columnOptions}
            jsonColumnProperties={jsonColumnProperties}
            searchRef={setFocusTarget}
            value={value}
            onChangeJsonColumnProperties={onChangeJsonColumnProperties}
            onChoose={apply}
            onClose={onClose}
            onReloadEligibleInlineMapperColumns={
              onReloadEligibleInlineMapperColumns
            }
          />
        );
      default: {
        return selection.type satisfies never;
      }
    }
  };

  return (
    <Row height="100%" width="100%">
      <MapperNav
        categories={categories}
        idPrefix={idPrefix}
        otherItems={otherItems}
        selection={selection}
        tabPanelId={tabPanelId}
        onSelect={select}
      />
      <Column
        aria-labelledby={getNavTabId(idPrefix, selection)}
        flex={1}
        id={tabPanelId}
        minWidth={0}
        role="tabpanel"
      >
        <Column key={getNavItemKey(selection)} flex={1} minHeight={0}>
          {renderPane()}
        </Column>
      </Column>
    </Row>
  );
}
